import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraViewer from "../../../../../../../../components/composed-styled-components/CameraViewer/CameraViewer";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

interface LotClosureTakerProps {
  initialImages?: string[];
  onImagesChange: (images: string[]) => void;
  disablebutton?: boolean;
}

const LotClosureTaker: React.FC<LotClosureTakerProps> = ({
  initialImages = [],
  onImagesChange,
  disablebutton = false
}) => {
  const [openCam, setOpenCam] = useState(false);
  const [images, setImages] = useState<string[]>([...initialImages]);
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);

  useEffect(() => {
    // Si cambian las iniciales, las sincronizamos
    setImages([...initialImages]);
  }, [initialImages]);

  const handleImageTaken = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      setImages(prev => {
        const next = [...prev, dataUrl];
        onImagesChange(next);
        return next;
      });
      setOpenCam(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = (idx: number) => {
    setImages(prev => {
      const next = prev.filter((_, i) => i !== idx);
      onImagesChange(next);
      return next;
    });
    // Si estabas viendo zoom en esa imagen, ciérralo
    if (zoomIdx === idx) {
      setZoomIdx(null);
    }
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        Cierre de lote
      </Typography>

      {/* Carrusel horizontal */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          pb: 2,
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.3)",
            borderRadius: 4
          }
        }}
      >
        {images.map((src, idx) => (
          <Box
            key={idx}
            sx={{
              position: "relative",
              flex: "0 0 auto",
              width: 200,
              height: 300,
              borderRadius: 1,
              boxShadow: 2,
              overflow: "hidden",
              cursor: "pointer",
              "&:hover img": { transform: "scale(1.05)" }
            }}
          >
            {/* Botón de eliminar */}
            <IconButton
              size="small"
              onClick={() => handleDelete(idx)}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                zIndex: 2,
                bgcolor: "rgba(255,255,255,0.8)",
                "&:hover": { bgcolor: "rgba(255,255,255,1)" }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <Box
              component="img"
              src={src}
              alt={`foto-${idx}`}
              onClick={() => setZoomIdx(idx)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.2s"
              }}
            />
          </Box>
        ))}
      </Box>

      {!disablebutton && (
        <Button variant="contained" onClick={() => setOpenCam(true)}>
          Capturar foto
        </Button>
      )}

      <CameraViewer
        open={openCam}
        onClose={() => setOpenCam(false)}
        onImageTaken={handleImageTaken}
      />

      <Dialog fullScreen open={zoomIdx !== null} onClose={() => setZoomIdx(null)}>
        <IconButton
          onClick={() => setZoomIdx(null)}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.5)",
            color: "white"
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          {zoomIdx !== null && (
            <TransformWrapper initialScale={1} wheel={{ step: 0.1 }}>
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
              >
                <Box
                  component="img"
                  src={images[zoomIdx]}
                  sx={{ maxWidth: "100%", maxHeight: "100%" }}
                />
              </TransformComponent>
            </TransformWrapper>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LotClosureTaker;
